import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, Search, Loader2, BarChart3, FileSpreadsheet } from 'lucide-react';
import * as XLSX from 'xlsx';
import { adminApi } from '../services/api.jsx';
import PiedTableau from '../components/PiedTableau';
import { useDroits } from '../context/Droits';
import {
  annoncerErreur, annoncerSucces, EtatErreur, EtatSansDonnees, Squelette, SqueletteTableau,
} from '../etats';

export default function Users() {
  // Les boutons d'export ne s'affichent que si le droit est ouvert.
  const { peut } = useDroits();
  const navigate = useNavigate();
  const [organisations, setOrganisations] = useState([]);
  const [structure, setStructure] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erreur, setErreur] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [orgsData, structData] = await Promise.all([
        adminApi.getOrganisations(),
        adminApi.getStructure()
      ]);
      setOrganisations(orgsData || []);
      setStructure(structData || []);
      setErreur(null);
    } catch (error) {
      console.error("Erreur de chargement:", error);
      setErreur(error);
    } finally {
      setLoading(false);
    }
  };

  const filteredOrgs = useMemo(() => {
    return organisations.filter(org => {
      const matchesSearch = org.nom.toLowerCase().includes(search.toLowerCase()) ||
                           (org.email && org.email.toLowerCase().includes(search.toLowerCase()));
      const matchesType = typeFilter === "all" || org.type_nom === typeFilter;
      return matchesSearch && matchesType;
    });
  }, [organisations, search, typeFilter]);

  const uniqueTypes = useMemo(() => {
    return ["all", ...new Set(organisations.map(o => o.type_nom))];
  }, [organisations]);

  const handleExportConsolidatedAudit = async () => {
    if (filteredOrgs.length === 0) return;
    setExporting(true);

    try {
      const detailedAudits = await Promise.all(
        filteredOrgs.map(async (org) => {
          try {
            const diag = await adminApi.getDiagnosticByOrg(org.id);
            return { org, diag };
          } catch (e) {
            return { org, diag: null };
          }
        })
      );

      const wb = XLSX.utils.book_new();
      const identitiesData = filteredOrgs.map(org => ({
        "ID UNIQUE": org.id,
        "NOM DE LA STRUCTURE": org.nom,
        "TYPE / SECTEUR": org.type_nom,
        "EMAIL CONTACT": org.email || 'N/A',
        "VILLE": org.ville,
        "PAYS": org.pays,
        "DATE INSCRIPTION": new Date(org.date_creation).toLocaleDateString('fr-FR')
      }));
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(identitiesData), "1. Structures");

      const synthesisData = detailedAudits.map(({ org, diag }) => ({
        "NOM STRUCTURE": org.nom,
        "SCORE GLOBAL (%)": diag ? Math.round(diag.score_total) : "N/A",
        "NIVEAU DE MATURITÉ": diag ? diag.niveau : "Non audité",
        "RECO/SYNTHÈSE EXPERT": diag ? diag.restitution : "Données manquantes"
      }));
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(synthesisData), "2. Synthèse Stratégique");

      structure.forEach((domaine, idx) => {
        const domainRows = detailedAudits.map(({ org, diag }) => {
          const row = { "Organisation": org.nom };
          domaine.subdomains.forEach(sub => {
            const scoreVal = (diag && diag.domain_scores && diag.domain_scores[domaine.nom])
              ? `${Math.round(diag.domain_scores[domaine.nom])}%`
              : "N/A";
            row[sub.nom] = scoreVal;
          });
          return row;
        });
        const wsDom = XLSX.utils.json_to_sheet(domainRows);
        const sheetName = `${idx + 3}. ${domaine.nom.substring(0, 25)}`;
        XLSX.utils.book_append_sheet(wb, wsDom, sheetName);
      });

      // Export des actions correctives telles qu'en base de données
      const actionsData = [];
      detailedAudits.forEach(({ org, diag }) => {
        if (diag && diag.actions_correctives) {
          diag.actions_correctives.forEach(a => {
            actionsData.push({
              "STRUCTURE": org.nom,
              "PRIORITÉ": a.priorite,
              "DOMAINE": a.sous_domaine,
              "ACTION RECOMMANDÉE": a.action || a.description
            });
          });
        }
      });
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(actionsData), "Plan Action Global");

      XLSX.writeFile(wb, `REPORTING_CONSOLIDE_ASSOPRO_${new Date().toISOString().split('T')[0]}.xlsx`);
      annoncerSucces(`Audit consolidé exporté (${filteredOrgs.length} organisation${filteredOrgs.length > 1 ? 's' : ''}).`);
    } catch (error) {
      console.error("Erreur Export:", error);
      annoncerErreur("Erreur lors de la génération du rapport consolidé.");
    } finally {
      setExporting(false);
    }
  };

  // Le chargement n'a lieu qu'une fois : le squelette ne peut rien effacer.
  const enChargement = loading;

  return (
    <div className="space-y-8 animate-in">
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-end gap-6">
        <div>
          <h1 className="titre-page">
            Organisations
          </h1>
          <p className="text-[var(--text-muted)] font-medium mt-2">Pilotage et consolidation des diagnostics stratégiques.</p>
        </div>

        {peut('organisations', 'exporter') && (
          <button
            onClick={handleExportConsolidatedAudit}
            disabled={exporting || enChargement || filteredOrgs.length === 0}
            aria-busy={exporting || undefined}
            className="act act-contour"
          >
            {exporting ? <Loader2 className="w-4 h-4 animate-spin" />
                       : <FileSpreadsheet className="w-4 h-4" />}
            {exporting ? 'Consolidation en cours…' : "Exporter l'audit consolidé (.xlsx)"}
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="md:col-span-2 relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--text-muted)]" />
          <input
            type="text"
            placeholder="Rechercher une structure..."
            className="champ champ-loupe font-bold"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="relative">
          <select
            className="filtre"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
          >
            {uniqueTypes.map(t => <option key={t} value={t} className="bg-[var(--bg-card)] text-[var(--text-bold)]">{t === 'all' ? 'Tous les secteurs' : t}</option>)}
          </select>
        </div>
        <div className="bg-[var(--accent)]/5 border border-[var(--accent)]/20 rounded-pastille flex flex-col items-center justify-center">
            <span className="text-[9px] font-semibold uppercase text-[var(--text-muted)]">Structures</span>
            {enChargement
              ? <Squelette className="h-5 w-8 mt-1" />
              : <span className="text-lg font-semibold text-[var(--accent-texte)]">{filteredOrgs.length}</span>}
        </div>
      </div>

      {enChargement ? (
        <SqueletteTableau colonnes={5} />
      ) : erreur ? (
        <div className="carte-tableau"><EtatErreur erreur={erreur} onReessayer={loadData} /></div>
      ) : (
      <div className="carte-tableau">
        <div className="tableau-hote">
          <table className="tableau-cartes w-full text-left">
            <thead className="">
              <tr>
                <th className="">Organisation</th>
                <th className="">Type</th>
                <th className="">Coordonnées</th>
                <th className="">Zone</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--border)]">
              {filteredOrgs.map(org => (
                <tr key={org.id} className="hover:bg-[var(--bg-main)] transition-all group">
                  <td data-libelle="Organisation" className="px-8 py-6">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-pastille bg-[var(--bg-card)] flex items-center justify-center text-[var(--accent-texte)] font-semibold border border-[var(--border)] group-hover:bg-[var(--accent)] group-hover:text-[#0A1128] transition-all">{org.nom[0]}</div>
                      <div className="flex flex-col">
                        <span className="text-sm font-bold text-[var(--text-bold)] uppercase group-hover:text-[var(--accent-texte)] transition-colors">{org.nom}</span>
                        <span className="text-[9px] font-semibold text-[var(--text-muted)] uppercase">ID: {org.id}</span>
                      </div>
                    </div>
                  </td>
                  <td data-libelle="Type" className="px-8 py-6 text-[10px] font-semibold text-[var(--text-muted)]">{org.type_nom}</td>
                  <td data-libelle="Coordonnées" className="px-8 py-6 text-xs font-medium text-[var(--text-muted)]">{org.email || 'N/A'}</td>
                  <td data-libelle="Zone" className="px-8 py-6 text-xs font-bold text-[var(--text-muted)]">{org.ville}, {org.pays}</td>
                  <td data-actions="" className="px-8 py-6 text-right">
                    <button
                      onClick={() => navigate(`/users/${org.id}`)}
                      className="act act-tertiaire"
                    >
                      <BarChart3 className="w-4 h-4" />
                      <span className="surtitre">Expertise</span>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {filteredOrgs.length === 0 ? (
          organisations.length === 0 ? (
            <EtatSansDonnees cas="vide" icone={Building2} titre="Aucune organisation inscrite"
              aide="Les organisations apparaîtront ici après leur premier diagnostic." />
          ) : search.trim() ? (
            <EtatSansDonnees cas="sansResultat" titre={`Aucune organisation pour « ${search.trim()} »`}
              action={<button type="button" className="act act-sm act-contour" onClick={() => setSearch('')}>Effacer la recherche</button>} />
          ) : (
            <EtatSansDonnees cas="sansFiltre" titre="Aucune organisation dans ce secteur"
              action={<button type="button" className="act act-sm act-contour" onClick={() => setTypeFilter('all')}>Réinitialiser</button>} />
          )
        ) : (
          <PiedTableau affiches={filteredOrgs.length} total={organisations.length}
                       nom="organisations" />
        )}
      </div>
      )}
    </div>
  );
}
